import React, {useState} from "react";
import {Box, Typography, Button} from "@mui/material";
import Grid from '@mui/material/Grid';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {styled, keyframes} from "@mui/system";
import moment from "moment-timezone";
import {useMutation} from "@apollo/client";
import {green, blue, red} from "@mui/material/colors";

import {MY_SHIFT_AND_BOOKINGS, PERFORMER_SELF_CHECKIN} from "../../queries/GraphQLQueries";
import {COMPACT_TIME_FORMAT} from "../../config/DateTimeConstants";
import {MOMENT_UNIT_MINUTES} from "../../domain/ShiftPlanConstants";

const CARD_HEADER_COLOR = blue[50];
const UPCOMING_SHIFT_ICON_COLOR = green[500];
const CHECK_IN_BUTTON_COLOR = green[700];
const CHECK_IN_BUTTON_ANIMATION_DURATION = '2s infinite';

const ROUNDING_OFF_UPCOMING_SHIFT_END_TIME = 1;
const LATE_CHECK_IN_MARGIN_MINUTES = 10;

const upcomingShiftIconStyle = {
  color: UPCOMING_SHIFT_ICON_COLOR,
  fontSize: "1.25rem",
  transform: "translate(0.5rem, 0)"
};

const isPaidCheckInWindowOver = (performingFrom) => moment().isSameOrAfter(moment(performingFrom).subtract(LATE_CHECK_IN_MARGIN_MINUTES, 'minutes'));

const dimPulse = (performingFrom) => keyframes`
  0%, 100% {     
    background-color: ${isPaidCheckInWindowOver(performingFrom) ? red[700] : green[700] }
  }

  50% {    
    background-color: ${isPaidCheckInWindowOver(performingFrom) ? red[200] : green[200] }
  }
`;

const checkInButtonStyle = (performingFrom) => ({
  width: { xs: "100%", md: "30%", lg: "40%" },
  fontSize: "1.125rem",
  bgcolor: CHECK_IN_BUTTON_COLOR,
  color: "white",
  fontWeight: "bold",
  animation: `${dimPulse(performingFrom)} ${CHECK_IN_BUTTON_ANIMATION_DURATION}`,
});

const containerStyle = {
  bgcolor: CARD_HEADER_COLOR,
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.5rem",
};

const cardHeaderStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem"
}

const ServiceTypeGrid = styled(Grid)(() => ({
  backgroundColor: "black",
  width: "2.5rem",
  borderRadius: "0 0.719rem 0.719rem 0",
  padding: "0.1rem",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  justifyContent: "center",
  marginLeft:  "-0.625rem"
}));

const cardTextStyle = {
  pl: '1rem',
  fontWeight: "bold",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center"
}

export default function PerformerSelfCheckInCard({currentShift}) {
  const [isPerformerSelfCheckInInProgress, setIsPerformerSelfCheckInInProgress] = useState(false);

  const [performerSelfCheckIn] = useMutation(PERFORMER_SELF_CHECKIN, {
    refetchQueries: [{ query: MY_SHIFT_AND_BOOKINGS }, 'GetMyShiftAndBookings'],
    onCompleted: () => setIsPerformerSelfCheckInInProgress(false)
  });

  const handlePerformerCheckIn = () => {
    setIsPerformerSelfCheckInInProgress(true);
    performerSelfCheckIn({ variables: { shiftId: currentShift.id } });
  };

  const upcomingShiftStartTime = moment(currentShift.performingFrom).format(COMPACT_TIME_FORMAT);
  const upcomingShiftEndTime = moment(currentShift.end).add(ROUNDING_OFF_UPCOMING_SHIFT_END_TIME, MOMENT_UNIT_MINUTES).format(COMPACT_TIME_FORMAT);

  return (
    <Grid sx={containerStyle}>
      <Box sx={cardHeaderStyle}>
        <ServiceTypeGrid><CheckCircleIcon sx={upcomingShiftIconStyle} /></ServiceTypeGrid>

        <Typography sx={cardTextStyle}>
          Upcoming Shift:&nbsp;<Typography>{upcomingShiftStartTime} - {upcomingShiftEndTime}</Typography>
        </Typography>
      </Box>

      <Button onClick={handlePerformerCheckIn} disabled={isPerformerSelfCheckInInProgress} sx={checkInButtonStyle(currentShift.performingFrom)}>
        Check In
      </Button>
    </Grid>
  );
}
